/**
 * docxToPlate — convert an uploaded `.docx` file into a Plate fragment.
 *
 * Pipeline (mirrors the editor's existing Word-HTML PASTE path so imported and
 * pasted Word content behave identically):
 *
 *   .docx (zip)  ──mammoth──▶  Word HTML  ──sanitizeOfficeHtml──▶  clean HTML
 *        ──strip data-slate-*──▶  ──deserializeHtmlElement(editor)──▶  Plate fragment
 *
 * Why mammoth: a `.docx` is a zipped OOXML bundle, not HTML. mammoth unzips it
 * and emits semantic HTML (headings, paragraphs, lists, tables, bold/italic,
 * inline images as base64 data URLs) — exactly the shape the editor's HTML
 * deserializer already understands. We deliberately reuse `sanitizeOfficeHtml`
 * + `deserializeHtmlElement` rather than a bespoke OOXML parser so there is ONE
 * Word-ingestion code path to maintain.
 *
 * Images: mammoth inlines images as `data:` URLs by default; the deserializer
 * turns those into Plate image nodes, and the surface's existing
 * save pipeline (processImagesBeforeSave) uploads them on save — same as a
 * pasted data-URL image. No extra handling needed here.
 */
import { collapseWhiteSpace, deserializeHtmlElement } from 'platejs';
import mammoth from 'mammoth';
import { sanitizeOfficeHtml } from './sanitizeOfficeHtml';
import { convertLayoutTablesToColumns } from './layoutTables';

export interface DocxImportResult {
    fragment: any[];
    /** Non-fatal conversion notes from mammoth (unsupported styles, etc.). */
    messages: string[];
}

/**
 * Convert a `.docx` File/Blob into a Plate fragment ready for
 * `editor.tf.insertFragment(...)`. Throws if the file isn't a readable docx.
 */
export async function docxFileToFragment(editor: any, file: File | Blob): Promise<DocxImportResult> {
    const arrayBuffer = await file.arrayBuffer();

    // mammoth → Word HTML. `convertToHtml` resolves even for messy docs,
    // collecting any warnings in `result.messages` rather than throwing.
    const result = await mammoth.convertToHtml({ arrayBuffer });
    const html = result?.value ?? '';
    const messages = Array.isArray(result?.messages) ? result.messages.map((m: any) => m?.message ?? String(m)) : [];

    if (!html.trim()) {
        return { fragment: [], messages };
    }

    // Reuse the same cleaning the paste path applies (strips Office cruft,
    // <style> blocks, conditional comments, …).
    const cleaned = sanitizeOfficeHtml(html);
    const parsed = new DOMParser().parseFromString(cleaned, 'text/html');
    const body = parsed?.body;
    if (!body) return { fragment: [], messages };

    // Mirror handlePaste: strip `data-slate-*` markers so Plate runs its normal
    // HTML parse rules instead of treating nodes as "already slate" (which would
    // silently drop block wrappers). mammoth doesn't emit these, but this keeps
    // the two ingestion paths byte-identical and future-proof.
    body.querySelectorAll('[data-slate-node], [data-slate-leaf], [data-slate-string], [data-slate-fragment]').forEach((el) => {
        el.removeAttribute('data-slate-node');
        el.removeAttribute('data-slate-leaf');
        el.removeAttribute('data-slate-string');
        el.removeAttribute('data-slate-fragment');
        el.removeAttribute('data-slate-type');
        el.removeAttribute('data-slate-inline');
        el.removeAttribute('data-slate-void');
        el.removeAttribute('data-slate-marks');
    });

    // Match the paste path: run Plate's whitespace collapsing before the bare
    // element mapper (deserializeHtmlElement skips the collapseWhiteSpace step
    // that the full deserializeHtml dispatcher runs).
    let normalizedBody: HTMLElement = body;
    try {
        normalizedBody = collapseWhiteSpace(body);
    } catch {
        normalizedBody = body;
    }

    const fragment: any = (deserializeHtmlElement as any)(editor, normalizedBody);

    // Word uses tables for column layout (résumé panels, "label | value" grids,
    // empty spacer columns). Convert single-row layout tables into Plate column
    // groups so the side-by-side layout is preserved and editable; genuine data
    // tables are left intact.
    const withColumns = convertLayoutTablesToColumns(Array.isArray(fragment) ? fragment : []);
    return { fragment: withColumns, messages };
}
